import type { GameEvent, MatchEvent } from '@chessmate/protocol';

const BASE = import.meta.env.VITE_API_URL ?? '';

export interface SubscribeOptions {
  readonly token: string;
  readonly onEvent: (event: GameEvent) => void;
  /** Test seam — defaults to the browser EventSource. */
  readonly sourceFactory?: (url: string) => EventSource;
}

/** Opens the SSE stream for a game and returns an unsubscribe function. */
export function subscribeGame(gameId: string, opts: SubscribeOptions): () => void {
  const url = `${BASE}/api/games/${encodeURIComponent(gameId)}/events?token=${encodeURIComponent(opts.token)}`;
  const source = opts.sourceFactory ? opts.sourceFactory(url) : new EventSource(url);
  const handler = (raw: Event) => {
    const message = raw as MessageEvent<string>;
    try {
      const event = JSON.parse(message.data) as GameEvent;
      opts.onEvent(event);
    } catch {
      // Malformed frames are ignored — the server never sends them.
    }
  };
  source.addEventListener('game:update', handler);
  source.addEventListener('game:move', handler);
  source.addEventListener('game:ended', handler);
  return () => source.close();
}

export interface SubscribeMatchmakingOptions {
  readonly onEvent: (event: MatchEvent) => void;
  /** Test seam — defaults to the browser EventSource. */
  readonly sourceFactory?: (url: string) => EventSource;
}

/** Opens the public SSE stream for pair notifications; returns an unsubscribe function. */
export function subscribeMatchmaking(token: string, opts: SubscribeMatchmakingOptions): () => void {
  const url = `${BASE}/api/matchmaking/events?token=${encodeURIComponent(token)}`;
  const source = opts.sourceFactory ? opts.sourceFactory(url) : new EventSource(url);
  const handler = (raw: Event) => {
    try {
      opts.onEvent(JSON.parse((raw as MessageEvent<string>).data) as MatchEvent);
    } catch {
      // Malformed frames are ignored.
    }
  };
  source.addEventListener('match:queued', handler);
  source.addEventListener('match:paired', handler);
  return () => source.close();
}
